import { updateArray } from '../../updateArray';
import { Translatable, PartyState } from '../../_generatedTypes.g';
import { api } from '../../api';
import { UnknownAction, Dispatch } from 'redux';
import { operationStatusSet, setOperationStatusWithDuration } from '../utils/operationSlice';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { voterEntityStateRefreshed, innerStateSet } from '../innerState/innerStateSlice';

export const deleteParty = async (partyId: number, token: string, dispatch: Dispatch<UnknownAction>) => {
    dispatch(operationStatusSet({ warning: false, message: 'Deleting party...', errors: [] }));
    return await api(token).Parties.Delete<void>(partyId, {})
        .ok(() => {
            setOperationStatusWithDuration({ warning: false, message: 'Deleted party successfully', errors: [] }, 3, dispatch);
            dispatch(partyDeleted(partyId));
        })
        .fail(() => dispatch(operationStatusSet({ warning: true, message: 'Something went wrong while attempting to delete this party', errors: [] })))
}

const partiesSlice = createSlice({
    name: 'SLICE_parties',
    initialState: {} as PartyState[],
    reducers: {
        partyReplaced(state, action: PayloadAction<PartyState>) {
            return updateArray(state, {
                with: _ => action.payload,
                where: p => p.PartyId === action.payload.PartyId,
            });
        },
        partyNameChanged(state, action: PayloadAction<{ newName: Translatable, rowVersion: string, partyId: number }>) {
            return updateArray(state, {
                with: p => ({
                    ...p,
                    Name: { ...p.Name, ...action.payload.newName },
                    RowVersion: action.payload.rowVersion,
                }),
                where: p => p.PartyId === action.payload.partyId,
            });
        },
        partyAdminAdded(state, action: PayloadAction<{ userName: string, partyId: number }>) {
            return updateArray(state, {
                with: p => ({
                    ...p,
                    Administrators: [
                        action.payload.userName,
                        ...p.Administrators,
                    ],
                }),
                where: p => p.PartyId === action.payload.partyId
            });
        },
        partyAdminRemoved(state, action: PayloadAction<{ userName: string, partyId: number }>) {
            return updateArray(state, {
                with: p => ({
                    ...p,
                    Administrators: p.Administrators.filter(x => x !== action.payload.userName),
                }),
                where: p => p.PartyId === action.payload.partyId
            });
        },
        partyDeleted(state, action: PayloadAction<number>) {
           return state.filter(x => x.PartyId !== action.payload);
        }
    },
    extraReducers: (builder) => {
        builder.addCase(innerStateSet, (state, action) => {
            if (action.payload.Type === 'LOGGED_IN_STATE') {
                return action.payload.VoterEntities.Parties;
            } else {
                return [];
            }
        });
        builder.addCase(voterEntityStateRefreshed, (state, action) => action.payload.Parties);
    },
});

export const {
    partyReplaced,
    partyNameChanged,
    partyAdminAdded,
    partyAdminRemoved,
    partyDeleted,
} = partiesSlice.actions

export default partiesSlice.reducer
